import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import { suggestSkillsFromAnswers, type UserSkillEntry } from '@/api/teamSkills';
import { notifications } from '@/utils/notifications';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onApply: (skills: UserSkillEntry[]) => void;
};

const QUESTION_KEYS = ['workAreas', 'toolsAndTech', 'avoidTasks'] as const;
type QuestionKey = (typeof QUESTION_KEYS)[number];

export function TeamSkillsAiWizard({ isOpen, onClose, onApply }: Props) {
  const { t } = useTranslation('settings');
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<QuestionKey, string>>({
    workAreas: '',
    toolsAndTech: '',
    avoidTasks: '',
  });
  const [generating, setGenerating] = useState(false);

  useDialogA11y(dialogRef, { isOpen, onClose, disabled: generating });

  const reset = useCallback(() => {
    setStep(0);
    setAnswers({ workAreas: '', toolsAndTech: '', avoidTasks: '' });
    setGenerating(false);
  }, []);

  const handleClose = useCallback(() => {
    if (generating) return;
    reset();
    onClose();
  }, [generating, onClose, reset]);

  const currentKey = QUESTION_KEYS[step];
  const isOptionalStep = currentKey === 'avoidTasks';
  const canContinue =
    isOptionalStep || answers[currentKey].trim().length >= 3;

  const finish = async () => {
    if (!answers.workAreas.trim() || !answers.toolsAndTech.trim()) {
      notifications.error(t('skills.ai.requiredAnswers'));
      return;
    }
    setGenerating(true);
    try {
      const skills = await suggestSkillsFromAnswers({
        workAreas: answers.workAreas.trim(),
        toolsAndTech: answers.toolsAndTech.trim(),
        avoidTasks: answers.avoidTasks.trim() || undefined,
      });
      onApply(skills);
      notifications.success(t('skills.ai.success'));
      reset();
      onClose();
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : t('skills.ai.failed'));
    } finally {
      setGenerating(false);
    }
  };

  const goNext = () => {
    if (step < QUESTION_KEYS.length - 1) {
      setStep((s) => s + 1);
      return;
    }
    void finish();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        aria-label={t('skills.ai.close')}
        onClick={handleClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="skills-ai-wizard-title"
        className="relative z-10 w-full max-w-lg rounded-2xl border border-border bg-card shadow-modal"
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-primary">
              {t('skills.ai.step', { current: step + 1, total: QUESTION_KEYS.length })}
            </p>
            <h2 id="skills-ai-wizard-title" className="text-base font-black text-foreground">
              {t('skills.ai.title')}
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">{t('skills.ai.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={generating}
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted disabled:opacity-50"
            aria-label={t('skills.ai.close')}
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <label className="block text-sm font-bold text-foreground" htmlFor="skills-ai-answer">
            {t(`skills.ai.questions.${currentKey}`)}
            {isOptionalStep ? (
              <span className="ml-1 text-xs font-medium text-muted-foreground">
                ({t('skills.ai.optional')})
              </span>
            ) : null}
          </label>
          <textarea
            id="skills-ai-answer"
            rows={4}
            value={answers[currentKey]}
            disabled={generating}
            onChange={(e) =>
              setAnswers((prev) => ({ ...prev, [currentKey]: e.target.value }))
            }
            placeholder={t(`skills.ai.placeholders.${currentKey}`)}
            className="w-full resize-none rounded-xl border border-border bg-muted/20 px-3.5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canContinue && !generating) {
                e.preventDefault();
                goNext();
              }
            }}
          />
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/10 px-5 py-4">
          <button
            type="button"
            disabled={generating || step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="rounded-xl px-4 py-2 text-sm font-bold text-muted-foreground hover:bg-muted disabled:opacity-40"
          >
            {t('skills.ai.back')}
          </button>
          <button
            type="button"
            disabled={!canContinue || generating}
            onClick={() => void goNext()}
            className={clsx(
              'inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50'
            )}
          >
            {generating ? (
              <Loader2 size={16} className="animate-spin" aria-hidden />
            ) : (
              <Sparkles size={16} aria-hidden />
            )}
            {step < QUESTION_KEYS.length - 1 ? t('skills.ai.next') : t('skills.ai.generate')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
